import { useCallback, useRef } from "react";
import useSeatmapStore from "../../../../../../../../../stores/seatmapStore";
import "./zoomLevel.scss";
import { useActions } from "../ActionsProvider";

export default function ZoomLevel() {
  // Zustand
  const setScale = useSeatmapStore((state) => state.setScale);
  const scale = useSeatmapStore((state) => state.scale);

  const { stageRef, SCALE_LIMITS, SCALE_FACTOR, calculateScalePercentage } =
    useActions();

  const isAnimating = useRef(false);
  const lastClickTime = useRef(0);
  const CLICK_DELAY = 150; // ms between allowed clicks

  const handleZoom = useCallback(
    (zoomIn) => {
      const now = performance.now();

      // Prevent rapid clicks and concurrent animations
      if (isAnimating.current || now - lastClickTime.current < CLICK_DELAY) {
        return;
      }

      const stage = stageRef.current;
      if (!stage) return;

      const storeScale = useSeatmapStore.getState().scale;
      const oldScale =
        typeof storeScale === "number" && storeScale > 0
          ? storeScale
          : stage.scaleX();
      const dynamicScaleFactor = zoomIn ? SCALE_FACTOR : 1 / SCALE_FACTOR;

      // Calculate new scale
      let newScale = oldScale * dynamicScaleFactor;

      // Check scale limits
      const wouldBeScale = calculateScalePercentage(newScale);
      if (wouldBeScale > SCALE_LIMITS.MAX || wouldBeScale < SCALE_LIMITS.MIN) {
        return;
      }

      // Cache viewport dimensions
      const stageWidth = stage.width();
      const stageHeight = stage.height();
      const centerX = stageWidth / 2;
      const centerY = stageHeight / 2;

      // Calculate center point at current scale
      const centerOnStageX = (centerX - stage.x()) / oldScale;
      const centerOnStageY = (centerY - stage.y()) / oldScale;

      // Calculate new position
      const newPos = {
        x: -(centerOnStageX * newScale - centerX),
        y: -(centerOnStageY * newScale - centerY),
      };

      // Set animating flag
      isAnimating.current = true;
      lastClickTime.current = now;

      // Perform animation
      stage.to({
        scaleX: newScale,
        scaleY: newScale,
        x: newPos.x,
        y: newPos.y,
        duration: 0.18,
        onFinish: () => {
          isAnimating.current = false;
          stage.batchDraw();
          setScale(newScale);
        },
      });
    },
    [setScale, calculateScalePercentage, stageRef, SCALE_FACTOR, SCALE_LIMITS],
  );

  return (
    <div className="zoomContainer">
      <button className="zoomButton" onClick={() => handleZoom(false)}>
        -
      </button>
      <div>{calculateScalePercentage(scale)}%</div>
      <button className="zoomButton" onClick={() => handleZoom(true)}>
        +
      </button>
    </div>
  );
}
